import Button from "../Shared/Button";
import Input from "../Shared/Input";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { login, adminLogin, salesLogin } from "../../api/Auth/login";
import {
  isSafeInternalRedirect,
  rememberPostAuthRedirect,
  setAccessToken,
} from "../../lib/session";
import { AxiosError } from "axios";
import { ButtonTypes } from "../../utils/consts";
import { useUserDataStore } from "../../stores/user";
import { UpdateLatestCountry } from "../../api/Students/LatestCountry";
const validationSchema = Yup.object({
  username: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const LoginForm = () => {
  const country = localStorage.getItem("userCountry") || "EG";
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hostedQuizId = searchParams.get("hosted");
  const returnToCart = searchParams.get("cart");
  const redirect = searchParams.get("redirect");
  const isAdminRoute = location.pathname.includes("/login/admin");
  const isSalesRoute = location.pathname === "/login/sales";
  const { mutate: mutateCountry } = useMutation({
    mutationFn: () => UpdateLatestCountry({ lastCountry: country }),
    onSuccess: () => { },
    onError: () => { },

  });

  const { mutate, error, isPending } = useMutation({
    mutationFn: isAdminRoute ? adminLogin : isSalesRoute ? salesLogin : login, // Updated to include salesLogin
    onSuccess: (data) => {
      const { access_token, user, role } = data;
      setAccessToken(access_token, role);
      useUserDataStore.getState().setUserData({ ...user, role });
      if (hostedQuizId) {
        navigate(`/hosted-quiz/${hostedQuizId}`);
      } else if (returnToCart) {
        navigate(-1);
      } else if (isSafeInternalRedirect(redirect)) {
        navigate(redirect);
      } else if (isAdminRoute) {
        navigate("/dashboard/courses");
      } else if (isSalesRoute) {
        navigate("/sales/dashboard/students");
      } else if (
        role === "student" &&
        user.status?.toLowerCase() === "active"
      ) {
        navigate("/my-profile");
      } else {
        navigate("/");
      }
      if (role !== "admin" && role !== "sales") mutateCountry();
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });
  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const formattedValues = {
        ...values,
        username: values.username.toLowerCase(),
      };
      mutate(formattedValues);
    },
  });

  return (
    <div className="relative flex flex-col justify-center h-full text-burntBlack">
      <div className="mb-8 text-center md:text-start">
        <h1 className="font-semibold sm:mb-2 mb-1 sm:text-[42px] text-28px">
          Login To Your Account
        </h1>
        <p className="font-medium sm:text-xl">
          Welcome back! please insert your details
        </p>
      </div>
      <form
        onSubmit={formik.handleSubmit}
        className="flex flex-col gap-4 md:gap-5"
        action=""
      >
        <Input
          inputStyles="md:h-16 h-[52px]"
          value={formik.values.username}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.username && formik.errors.username
              ? formik.errors.username
              : ""
          }
          title="Email Address"
          placeholder="someone@example.com"
          name="username"
          type="text"
        />
        <Input
          inputStyles="md:h-16 h-[52px]"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.password && formik.errors.password
              ? formik.errors.password
              : ""
          }
          title="Password"
          placeholder="**********"
          name="password"
          type="password"
        />
        <Button
          type={ButtonTypes.SUBMIT}
          disabled={formik.isSubmitting || isPending}
          variant="primary"
          className="w-full md:mt-3 mt-2 flex justify-center items-center md:h-16 h-[54px]"
        >
          Sign In
          {isPending ? (
            <svg
              aria-hidden="true"
              className="inline w-8 h-8 ml-3 text-gray-200 animate-spin dark:text-gray-600 fill-secondary"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          ) : (
            ""
          )}
        </Button>
        {!(isAdminRoute || isSalesRoute) && (
          <>
            <div className="flex items-center justify-center gap-4">
              <div className="bg-gray-100 w-full h-0.5" />
              <span className="font-semibold text-gray-400">Or</span>
              <div className="bg-gray-100 w-full h-0.5" />
            </div>
            <button
              type="button"
              onClick={() => {
                rememberPostAuthRedirect(redirect);
                window.location.replace(
                  "https://nilewisect-production.up.railway.app/auth/google/login"
                );
              }}
              className="px-4 py-2 border md:h-16 h-[54px] items-center text-xl justify-center flex gap-2 border-slate-200 rounded-lg text-slate-700 font-medium hover:border-slate-400 hover:text-slate-900 hover:shadow transition duration-150"
            >
              <img
                className="w-6 h-6"
                src="https://www.svgrepo.com/show/475656/google-color.svg"
                loading="lazy"
                alt="google logo"
              />
              <span>Continue with Google</span>
            </button>
          </>
        )}
        {error instanceof AxiosError && (
          <span className="text-lg font-bold text-red-500">
            {error?.response?.data?.message || "An error occurred"}
          </span>
        )}
      </form>
      <Link
        to="/forget-password"
        className="font-semibold sm:text-0.5xl mt-5 text-primary"
      >
        Forgot Password ?
      </Link>
      {!(isAdminRoute || isSalesRoute) && (
        <p className="font-semibold sm:text-0.5xl w-full text-darkNavy absolute bottom-0 text-center">
          Dont Have An Account?{" "}
          <Link to={`/register${location.search}`} className="text-primary">
            Sign Up
          </Link>
        </p>
      )}
    </div>
  );
};

export default LoginForm;
